import { createContext, useState, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios'; // plain client for presigned S3 PUTs (no auth header — S3 rejects extra auth)
import api from '../utils/api';
import { TEMPLATE_DEFAULTS, SIZE_MAP, TEMPLATES } from '../utils/constants';
import { captureVideoPoster } from '../utils/capturePoster';
import { formatDuration, MAX_DURATION_SECONDS } from '../utils/fileValidation';
import { trackEvent } from '../utils/analytics';
import { clarityTag, clarityTags, clarityUpgrade, setStage, STAGE } from '../utils/clarity';
import { Clock } from 'lucide-react';

// Default caption style for first-time users / new uploads.
const DEFAULT_TEMPLATE_ID = 'ali-abdaal';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB per chunk
const UPLOAD_CONCURRENCY = 4; // chunks in flight at once — keeps the pipe full on high-latency routes

// The chunk size is part of the id (as a prefix so long filenames can't truncate
// it away): resume matches by fileId, and mixing part sizes from a session
// started under a different CHUNK_SIZE would assemble a corrupt file.
const generateFileId = (file) =>
  `c${CHUNK_SIZE}_${file.name}_${file.size}_${file.lastModified}`
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(0, 180);

const JobContext = createContext();

export const useJob = () => useContext(JobContext);

export const JobProvider = ({ children }) => {
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  // Browser-read duration of the file being uploaded, sent as an advisory hint
  // with /presign and chunk 0 so the server can reject an upload that cannot
  // fit the minute balance before any byte moves. Server-side ffprobe remains
  // the authoritative check.
  const durationHintRef = useRef(null);
  // Same value as state, for the processing screen's time estimate.
  const [mediaDurationSeconds, setMediaDurationSeconds] = useState(null);

  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  // Position of this job among not-yet-started jobs in the transcription queue
  // (1-based; null when the job isn't actually waiting on a slot — e.g. it's
  // already processing, or nothing else is ahead of it). Set from the SSE/poll
  // status payload in JobStatus.jsx; ProcessingScreen shows it while queued.
  const [queuePosition, setQueuePosition] = useState(null);
  const [queueTotal, setQueueTotal] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioProxyUrl, setAudioProxyUrl] = useState(null); // Optimized for waveform
  const [mediaType, setMediaType] = useState('audio'); // 'audio' or 'video'
  const [videoUrl, setVideoUrl] = useState(null);
  const [burning, setBurning] = useState(false);
  const [burnProgress, setBurnProgress] = useState(0);
  // burnJobId is set once when a burn starts and is NOT reset by navigation.
  // This prevents the race condition where jobId changes (user opens a different
  // editor) causing the polling to target the wrong job (which has burning:false
  // by default) and falsely declare burn completion.
  const [burnJobId, setBurnJobId] = useState(null);
  // When the current job's captions were last saved (ISO string). Lives here —
  // not in TranscriptEditor state — so the "export outdated" derivation survives
  // in-app navigation (re-entering the editor skips hydration when the context
  // still holds the job). Set from job.contentUpdatedAt on hydration and to
  // now() on every successful save.
  const [contentSavedAt, setContentSavedAt] = useState(null);
  // completedBurnUrl is set ONCE when burn polling confirms completion.
  // It carries the exact videoUrl from the API response that detected the burn
  // is done — no second fetch, no closure staleness, no race condition.
  // Consumers (TranscriptEditor auto-download) watch this and clear it after use.
  const [completedBurnUrl, setCompletedBurnUrl] = useState(null);
  // Explicit outcome of the LAST finished burn: 'success' | 'error' | null (in
  // flight / none yet). The pill must key off this — inferring success from
  // result.videoUrl shows "Export complete!" for a FAILED re-export, because the
  // previous successful burn's videoUrl is still sitting in result.
  const [burnOutcome, setBurnOutcome] = useState(null);
  const [fileName, setFileName] = useState(null);
  // Name of the file the CURRENT burn belongs to, captured when the burn starts.
  // fileName tracks whichever project is being viewed and the provider survives
  // navigation, so a burn finishing while another project is open must not be
  // labeled (pill) or downloaded (AutoDownloader) under the viewed project's name.
  const [burnFileName, setBurnFileName] = useState(null);
  // requestRating drives the post-export RatingModal. Set true (gated) once a burn
  // completes successfully; the modal reads it and clears it when dismissed/submitted.
  const [requestRating, setRequestRating] = useState(false);
  const burnPollFailRef = useRef(0);
  // Set while a cancel request is in flight. Cancel writes burning:false before it
  // answers, so a status poll landing in that window would read the canceled export
  // as FINISHED: announce success and auto-download the previous export's file.
  const burnPollPausedRef = useRef(false);
  const pauseBurnPolling = (paused) => { burnPollPausedRef.current = paused; };
  const lastProgressRef = useRef(0);
  const stallTimerRef = useRef(null);
  // The burn-poll effect is keyed on [burning, burnJobId] only, so its closure
  // would hold a stale jobId; the ref always carries the currently loaded job.
  const jobIdRef = useRef(jobId);
  useEffect(() => { jobIdRef.current = jobId; }, [jobId]);

  const [jobPreferences, setJobPreferences] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // True while the editor's centered export overlay is on screen — the corner
  // BurnProgressPill hides then, so only ONE progress indicator ever shows.
  const [burnOverlayVisible, setBurnOverlayVisible] = useState(false);
  const [refinedResult, setRefinedResult] = useState(null);
  // Set to the jobId when an upload was served from the duplicate-file cache, so the
  // editor can ask the user whether the reused captions are actually OK (sync/accuracy)
  // instead of silently handing back a possibly-stale result. Cleared once handled.
  const [justReusedJobId, setJustReusedJobId] = useState(null);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeWallForced, setUpgradeWallForced] = useState(false);
  // Why the pack wall opened, beyond "out of minutes": { reason: 'longVideo', videoMinutes,
  // capMinutes, ceilingMinutes } makes the modal lead with the Agency pack. null = minutes.
  const [upgradeWallReason, setUpgradeWallReason] = useState(null);

  // The "too long" refusal. Below the Agency cap it is an upsell: a toast that says how long the
  // video is, what the account allows, and offers the Agency pack in one click (the modal, not
  // the checkout: the user sees the price and what else the pack carries before paying). At the
  // 15-min ceiling no pack helps, so it is a plain ask to trim. The toaster is always white.
  const showVideoTooLongToast = ({ videoSeconds, capMinutes, ceilingMinutes, canUpgrade }) => {
    const length = videoSeconds ? formatDuration(videoSeconds) : null;
    if (!canUpgrade) {
      toast.error(`Videos can be up to ${capMinutes} minutes long. Please trim your video and upload again.`, { duration: 6000, id: 'video-too-long' });
      return;
    }
    toast((t) => (
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 shrink-0">
          <Clock className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-stone-900 font-display leading-snug">
            {length ? `This video is ${length}` : `This video is over ${capMinutes} minutes`}
          </p>
          <p className="text-xs text-stone-500 font-jakarta font-medium mt-1 leading-relaxed">
            Your plan takes videos up to {capMinutes} min. The Agency pack takes up to {ceilingMinutes} min per video.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                toast.dismiss(t.id);
                trackEvent('too_long_upsell_clicked', { seconds: Math.round(videoSeconds || 0), capMinutes });
                setUpgradeWallReason({ reason: 'longVideo', videoMinutes: videoSeconds ? Math.ceil(videoSeconds / 60) : null, capMinutes, ceilingMinutes });
                setUpgradeWallForced(false);
                setShowUpgradeModal(true);
              }}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold font-jetbrains transition-colors"
            >
              See Agency pack
            </button>
            <button
              type="button"
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-bold font-jetbrains text-stone-600 hover:bg-stone-100 transition-colors"
            >
              I&rsquo;ll trim it
            </button>
          </div>
        </div>
      </div>
    ), { duration: 12000, id: 'video-too-long' });
  };
  const [uploadConfig, setUploadConfig] = useState({ maxWords: 3, subtitleLanguage: 'hinglish', audioCleanup: false });

  // Ask for a rating after a successful export, but don't nag: skip if the user already
  // submitted feedback, or asked within the last 7 days. Tracked in localStorage so it
  // survives reloads. Wrapped in try/catch so a blocked localStorage never breaks export.
  const RATING_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
  const maybeRequestRating = () => {
    try {
      if (localStorage.getItem('feedback_submitted') === 'true') return;
      const lastAsked = Number(localStorage.getItem('feedback_last_asked') || 0);
      if (lastAsked && Date.now() - lastAsked < RATING_COOLDOWN_MS) return;
      localStorage.setItem('feedback_last_asked', String(Date.now()));
    } catch { /* localStorage unavailable — still show the prompt */ }
    setRequestRating(true);
  };

  // startBurn atomically marks the burn as active AND captures the jobId being burned.
  // Pass initialProgress > 0 when reconnecting to an in-progress burn after page refresh.
  // TranscriptEditor should call this instead of setBurning(true) directly.
  const startBurn = (jId, initialProgress = 0, name = null) => {
    setBurnJobId(jId);
    setBurnFileName(name ?? fileName); // Snapshot the burned job's name — see burnFileName above
    setCompletedBurnUrl(null); // Clear any previous burn's URL
    setBurnOutcome(null);      // Outcome of the previous burn no longer applies
    setBurnProgress(Math.max(0, Math.min(99, initialProgress || 0)));
    setBurning(true);
  };

  // Centralised burn polling — runs on any page so the pill always has fresh data.
  // Keyed on burnJobId (not jobId) so user navigating to a different editor
  // doesn't accidentally poll a non-burning job and misfire as complete.
  useEffect(() => {
    if (!burning || !burnJobId) return;
    // Every way an export gets watched (a new export, a reconnect after refresh)
    // starts here, so a pause left over from a finished cancel can't carry over.
    burnPollPausedRef.current = false;
    // A response that arrives after this effect was torn down belongs to an
    // export that is no longer being watched (canceled, or replaced by a new one)
    // and must not be applied to whatever runs now.
    let active = true;
    let pollInFlight = false;
    burnPollFailRef.current = 0;
    lastProgressRef.current = 0;
    // burnProgress is intentionally NOT reset to 0 here.
    // For a fresh burn it starts at 0 (default state / after resetJob).
    // For a reconnect (page refresh with burn in progress), startBurn pre-sets it
    // to the DB value so the bar shows immediately without a 3s blank gap.

    // Fire a warning toast if no progress update arrives within 90s
    const resetStallTimer = () => {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      stallTimerRef.current = setTimeout(() => {
        trackEvent('export_stalled');
        toast('Export is taking longer than expected it will complete in the background when ready.', { icon: '⏳', duration: 6000 });
      }, 90000);
    };
    resetStallTimer();

    const interval = setInterval(async () => {
      if (!active || pollInFlight || burnPollPausedRef.current) return;
      pollInFlight = true;
      try {
        // The burn endpoint returns only burning / progress / result.videoUrl /
        // result.burnError. No cache-buster: the response is no-store already,
        // and a stable URL lets the browser reuse the CORS preflight.
        const res = await api.get(`/api/status/${burnJobId}/burn`, { timeout: 15000 });
        // Checked again after the await: the request may have left before the
        // cancel started and read the job after cancel's write.
        if (!active || burnPollPausedRef.current) return;
        burnPollFailRef.current = 0;
        const d = res.data;
        if (d?.burning) {
          const newProgress = Math.min(99, d.progress || 0);
          if (newProgress !== lastProgressRef.current) {
            lastProgressRef.current = newProgress;
            resetStallTimer();
          }
          setBurnProgress(newProgress);
        }
        // Only declare complete when we see an explicit false AND the job has a
        // burn result (videoUrl or burnError) — guarding against default burning:false
        // on jobs that were never set to burning:true in the DB.
        if (d?.burning === false && (d?.result?.videoUrl || d?.result?.burnError !== undefined)) {
          active = false;
          clearInterval(interval);
          if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
          setBurning(false);
          // Success requires BOTH a videoUrl and no burnError: a failed re-export
          // keeps the PREVIOUS export's videoUrl in result (exports survive edits
          // now), so videoUrl alone would announce success and auto-download the
          // old file after a failed burn.
          if (d.result?.videoUrl && !d.result?.burnError) {
            setBurnProgress(100);
            // Only overwrite the loaded result if this burn belongs to the job the
            // user is currently viewing — the provider survives navigation now, so
            // the user may have opened a DIFFERENT project while the burn ran, and
            // job A's result must not masquerade as job B's.
            if (jobIdRef.current === burnJobId) setResult(d.result);
            setBurnOutcome('success');
            // Signal auto-download with the exact URL from THIS poll response.
            // This is the single source of truth — no second fetch needed.
            setCompletedBurnUrl(d.result.videoUrl);
            trackEvent('export_succeeded');
            // Prompt for a rating shortly after, so the download/toast lands first.
            setTimeout(() => maybeRequestRating(), 1500);
          } else {
            setBurnProgress(0);
            setBurnOutcome('error');
            const errMsg = d.result?.burnError;
            if (jobIdRef.current === burnJobId) {
              setResult(previous => ({ ...previous, burnError: errMsg || 'Export failed. Please try again.' }));
            }
            // Always keep the recording: a failed export is the complaint we
            // most need to be able to replay.
            trackEvent('export_failed', { reason: errMsg || 'unknown' });
            clarityUpgrade('export_failed');
            toast.error(errMsg ? `Export failed: ${errMsg}` : 'Export failed. Please try again.');
          }
        }
      } catch {
        if (!active || burnPollPausedRef.current) return;
        burnPollFailRef.current++;
        if (burnPollFailRef.current >= 10) {
          clearInterval(interval);
          if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
          setBurning(false);
          setBurnOutcome('error');
          trackEvent('export_failed', { reason: 'poll_connection_lost' });
          clarityUpgrade('export_failed');
          toast.error('Lost connection. Check export status and try again.');
        }
      } finally {
        pollInFlight = false;
      }
    }, 3000);
    return () => {
      active = false;
      clearInterval(interval);
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    };
  }, [burning, burnJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetJob = () => {
    setJobId(null);
    setStatus('idle');
    setProgress(0);
    setQueuePosition(null);
    setQueueTotal(null);
    setResult(null);
    setError(null);
    setAudioUrl(null);
    setAudioProxyUrl(null);
    setMediaType('audio');
    setVideoUrl(null);
    // Reset only the open editor/upload. Background exports own their state
    // and must keep polling/downloading while another project is opened.
    setContentSavedAt(null);
    setFileName(null);
    setJobPreferences(null);
    setHasUnsavedChanges(false);
    setRefinedResult(null);
    setJustReusedJobId(null);
  };

  // Read the media file's duration in the browser (metadata only, no decode).
  // Null when the browser can't read it; the server then checks after ffprobe.
  const getMediaDurationSeconds = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const el = document.createElement(file.type?.startsWith('audio') ? 'audio' : 'video');
      let settled = false;
      const done = (val) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(url);
        el.removeAttribute('src');
        resolve(val);
      };
      const timer = setTimeout(() => done(null), 5000);
      el.preload = 'metadata';
      // Millisecond precision: the gates ceil to the whole second, so rounding here could let a
      // 40.3s video past this check and then be refused by the server as needing 41s.
      el.onloadedmetadata = () => { clearTimeout(timer); done(Number.isFinite(el.duration) && el.duration > 0 ? Math.round(el.duration * 1000) / 1000 : null); };
      el.onerror = () => { clearTimeout(timer); done(null); };
      el.src = url;
    } catch { resolve(null); }
  });

  // Pre-flight minutes check so a user without the minutes for THIS video is
  // told to upgrade BEFORE any chunk leaves the browser, instead of after a
  // full (wasted) upload when /assemble returns 403. Mirrors the server's
  // exact-duration billing. Fails open on network errors — the
  // backend's checks remain the actual enforcement.
  const checkCreditsBeforeUpload = async (fileToUpload) => {
    durationHintRef.current = fileToUpload ? await getMediaDurationSeconds(fileToUpload) : null;
    setMediaDurationSeconds(durationHintRef.current);
    try {
      const { data: usage } = await api.get('/api/user/usage');
      if (usage.legacy || usage.bypass) return true;
      const available = usage.availableSeconds ?? 0;
      const hint = durationHintRef.current;

      // Video length cap first. Below the Agency cap (5 min accounts) the
      // answer is the Agency pack or a trim; at the 15-min ceiling only a trim.
      const cap = usage.maxVideoSeconds;
      if (cap && hint && hint > cap + 1) {
        const capMin = Math.round(cap / 60);
        const ceilingMin = Math.round(MAX_DURATION_SECONDS / 60);
        showVideoTooLongToast({ videoSeconds: hint, capMinutes: capMin, ceilingMinutes: ceilingMin, canUpgrade: cap < MAX_DURATION_SECONDS });
        trackEvent('upload_blocked_too_long', { seconds: Math.round(hint), capMinutes: capMin });
        return false;
      }

      // Exact billing mirrors the server: a video needs precisely its own
      // duration (ceiled to the second). No hint? Let the server decide.
      const needed = hint ? Math.ceil(hint) : 0;
      if (needed > 0 && available < needed) {
        toast.error(
          (usage.inflightSeconds ?? 0) > 0
            ? 'Not enough time left. Some is held by videos still processing. Buy a minute pack to upload more.'
            : available > 0
              ? `This video needs ${formatDuration(needed)} but you have ${formatDuration(available)} left. Buy a minute pack to continue.`
              : 'You are out of minutes. Buy a minute pack to upload this video.',
          { duration: 5000 }
        );
        // The paywall moment. Always keep this recording: it is the one place
        // where confusion costs a sale.
        trackEvent('minutes_exhausted', { needed, available, where: 'preupload' });
        clarityUpgrade('minutes_exhausted');
        setUpgradeWallReason(null);
        setUpgradeWallForced(true);
        setShowUpgradeModal(true);
        return false;
      }
      return true;
    } catch {
      return true; // usage endpoint unreachable — let the server decide at assemble
    }
  };

  // SHA-256 of the file, read in slices so a multi-GB file never sits in memory.
  // Runs alongside the S3 transfer and rides along on /complete. The server
  // used to compute this itself by pulling the whole object back from the
  // bucket, which took minutes for a large file and blew through Cloudflare's
  // 100 s request cap (the browser then saw a bogus CORS error while the job
  // was created anyway). Resolves null on any failure: the server just skips
  // duplicate detection for that upload.
  const HASH_SLICE_BYTES = 8 * 1024 * 1024;
  const hashFileSha256 = async (file, signal) => {
    const { createSHA256 } = await import('hash-wasm');
    const hasher = await createSHA256();
    hasher.init();
    for (let offset = 0; offset < file.size; offset += HASH_SLICE_BYTES) {
      if (signal?.aborted) return null;
      const buf = await file.slice(offset, offset + HASH_SLICE_BYTES).arrayBuffer();
      hasher.update(new Uint8Array(buf));
    }
    return hasher.digest('hex');
  };

  // /complete answered with no HTTP response at all (proxy timeout, dropped
  // connection). The server very likely finished and created the job after the
  // proxy gave up on us, so look for it by the server-generated filename before
  // reporting a failure; otherwise the user re-uploads and pays twice.
  const RECOVERY_POLL_MS = 5000;
  const RECOVERY_WINDOW_MS = 4 * 60 * 1000;
  const RECOVERY_MAX_UNREACHABLE = 3; // the API itself is down or we are offline: stop waiting
  const findJobByFilename = async ({ serverFilename, originalName, since }, signal) => {
    const deadline = Date.now() + RECOVERY_WINDOW_MS;
    let unreachable = 0;
    while (Date.now() < deadline) {
      if (signal?.aborted) return null;
      try {
        const res = await api.get('/api/jobs', { params: { limit: 20 }, signal });
        unreachable = 0;
        // Normal path: the job carries the presign filename. Duplicate-reuse
        // path: it carries the OLD job's filename, so also accept a job for this
        // original name created after this upload started.
        const job = (res.data?.jobs || []).find(j =>
          j.filename === serverFilename
          || (j.originalName === originalName && new Date(j.createdAt).getTime() >= since)
        );
        if (job) {
          return { jobId: job._id || job.id, mediaType: job.mediaType, recovered: true };
        }
      } catch (err) {
        if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return null;
        if (!err.response && ++unreachable >= RECOVERY_MAX_UNREACHABLE) return null;
      }
      await new Promise(r => setTimeout(r, RECOVERY_POLL_MS));
    }
    return null;
  };

  // Upload the file STRAIGHT to S3 via presigned URLs — the bucket (ap-south-1)
  // is network-close to our users, while the VPS route crawls at consumer-ISP
  // speeds. Returns the /complete response (same shape as /assemble). Throws an
  // error with .directUploadFailed = true when the direct path is unavailable
  // (S3 off, bucket unreachable, CORS misconfig) so the caller can fall back to
  // the legacy chunked upload through the VPS. Business rejections (credit
  // limits, file too large) are rethrown as-is and surface to the user.
  const directUploadToS3 = async (fileToUpload, config, styleOptions) => {
    const signal = abortControllerRef.current?.signal;
    const failFallback = () => {
      const e = new Error('Direct S3 upload unavailable');
      e.directUploadFailed = true;
      return e;
    };

    let presign;
    try {
      presign = await api.post('/api/upload/presign', {
        filename: fileToUpload.name,
        size: fileToUpload.size,
        mimeType: fileToUpload.type,
        // Advisory duration hint so the minute gate can size this upload
        // before any byte leaves the browser.
        ...(durationHintRef.current ? { durationSeconds: durationHintRef.current } : {}),
      }, { signal });
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') throw err;
      // Real rejections the user must see: auth, credit limits, file too large.
      // Everything else (501 S3 off, 500, 400 odd mime type the legacy path
      // tolerated, network error) falls back to the chunked upload.
      if ([401, 403, 413].includes(err.response?.status)) throw err;
      throw failFallback();
    }

    const { mode, filename, url, uploadId, partSize, urls } = presign.data;
    const uploadStartedAt = Date.now();
    const parts = [];
    // Hash concurrently with the transfer; awaited only when /complete is built.
    // Own controller so the hash stops if the S3 leg fails and we fall back to
    // the VPS path (which hashes server-side anyway).
    const hashAbort = new AbortController();
    const stopHash = () => hashAbort.abort();
    signal?.addEventListener('abort', stopHash, { once: true });
    const hashPromise = hashFileSha256(fileToUpload, hashAbort.signal).catch(() => null);

    try {
      if (mode === 'single') {
        await axios.put(url, fileToUpload, {
          // Content-Type is part of the presigned signature — must match exactly.
          headers: { 'Content-Type': fileToUpload.type },
          signal,
          onUploadProgress: (e) => {
            setProgress(Math.round((Math.min(e.loaded, fileToUpload.size) / fileToUpload.size) * 100));
          },
        });
      } else {
        const inFlightBytes = new Map(); // partNumber → bytes sent so far
        let completedBytes = 0;
        const reportProgress = () => {
          let inFlight = 0;
          for (const bytes of inFlightBytes.values()) inFlight += bytes;
          const total = Math.min(completedBytes + inFlight, fileToUpload.size);
          setProgress(Math.round((total / fileToUpload.size) * 100));
        };

        const uploadPart = async ({ partNumber, url: partUrl }) => {
          const start = (partNumber - 1) * partSize;
          const end = Math.min(start + partSize, fileToUpload.size);
          const resp = await axios.put(partUrl, fileToUpload.slice(start, end), {
            signal,
            onUploadProgress: (e) => {
              inFlightBytes.set(partNumber, Math.min(e.loaded, end - start));
              reportProgress();
            },
          });
          // S3 must expose the ETag header via bucket CORS; without it the
          // multipart upload can't be completed — fall back rather than hang.
          const etag = resp.headers?.etag;
          if (!etag) throw new Error('Missing ETag: bucket CORS not exposing it');
          parts.push({ PartNumber: partNumber, ETag: etag });
          inFlightBytes.delete(partNumber);
          completedBytes += (end - start);
          reportProgress();
        };

        let nextPart = 0;
        await Promise.all(Array.from(
          { length: Math.min(UPLOAD_CONCURRENCY, urls.length) },
          async () => {
            while (nextPart < urls.length) {
              if (signal?.aborted) return;
              await uploadPart(urls[nextPart++]);
            }
          }
        ));
      }
    } catch (s3Err) {
      stopHash();
      // Tell the server to discard the partial upload (fire-and-forget).
      api.post('/api/upload/abort', { filename, uploadId }).catch(() => {});
      if (s3Err.code === 'ERR_CANCELED' || s3Err.name === 'CanceledError') throw s3Err;
      throw failFallback();
    }

    if (signal?.aborted) {
      api.post('/api/upload/abort', { filename, uploadId }).catch(() => {});
      const e = new Error('canceled');
      e.name = 'CanceledError';
      throw e;
    }

    // Finalize: server completes the multipart upload, pulls the file from S3 and
    // runs the normal processing pipeline. Errors here (e.g. credit limit) are
    // real rejections and propagate to the caller's standard error handling.
    const uploadMs = Date.now() - uploadStartedAt;
    // A slow pipe is the single most common reason a session looks "stuck", and
    // it is invisible in a recording. Tag the speed so those sessions are
    // findable, and mark the really slow ones on the timeline.
    const mbps = uploadMs > 0 ? (fileToUpload.size * 8) / uploadMs / 1000 : null;
    if (mbps !== null) {
      clarityTag('uploadSpeed', mbps < 1 ? 'under1mbps' : mbps < 5 ? '1to5mbps' : 'over5mbps');
      if (mbps < 1) trackEvent('upload_slow', { mbps: Math.round(mbps * 100) / 100 });
    }
    // The hash normally finishes long before the transfer. If it is still
    // running (slow disk, fast LAN), do not hold the upload hostage: give it a
    // bounded grace period, then go without and let the server skip dedup.
    const HASH_GRACE_MS = 30000;
    const fileHash = await Promise.race([
      hashPromise,
      new Promise(r => setTimeout(() => r(null), HASH_GRACE_MS)),
    ]);
    if (!fileHash) stopHash();
    const completeBody = {
      filename,
      uploadId,
      parts,
      originalName: fileToUpload.name,
      mimeType: fileToUpload.type,
      uploadMs,
      // Content hash for duplicate-upload reuse; the server no longer computes it.
      ...(fileHash ? { fileHash } : {}),
      // Lets the server's first minutes gate size this video (ffprobe confirms it right after).
      ...(durationHintRef.current ? { durationSeconds: durationHintRef.current } : {}),
      maxWords: config.maxWords,
      subtitleLanguage: config.subtitleLanguage,
      audioCleanup: !!config.audioCleanup,
      styleOptions: JSON.stringify(styleOptions),
    };
    try {
      return await api.post('/api/upload/complete', completeBody, { signal });
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') throw err;
      // A real HTTP status (401, 403 credit limit, 413...) is a genuine rejection.
      if (err.response) throw err;
      const recovered = await findJobByFilename({
        serverFilename: filename,
        originalName: fileToUpload.name,
        since: uploadStartedAt,
      }, signal);
      if (recovered) {
        trackEvent('upload_complete_recovered');
        return { data: recovered };
      }
      throw err;
    }
  };

  // Legacy fallback: upload through the VPS in 10 MB chunks with resume support,
  // then /assemble creates the job. Used when the direct S3 path is unavailable.
  // Returns the /assemble response, or null if the user aborted mid-upload.
  const chunkedUploadToVPS = async (fileToUpload, config, styleOptions) => {
    const fileId = generateFileId(fileToUpload);
    const totalChunks = Math.ceil(fileToUpload.size / CHUNK_SIZE);

    // Check which chunks already reached the server (enables resume on re-upload)
    let uploadedSet = new Set();
    try {
      const statusRes = await api.get(`/api/upload/chunks/${encodeURIComponent(fileId)}`);
      (statusRes.data.uploadedChunks || []).forEach(i => uploadedSet.add(i));
    } catch {
      // Network error — start fresh, all chunks will be uploaded
    }

    let uploadedBytes = Math.min(uploadedSet.size * CHUNK_SIZE, fileToUpload.size);
    setProgress(Math.round((uploadedBytes / fileToUpload.size) * 100));

    // Upload missing chunks with limited parallelism. Sequentially the link sits
    // idle for a full round trip between chunks, which dominates upload time on
    // high-latency routes (user → Helsinki VPS).
    const pendingChunks = [];
    for (let i = 0; i < totalChunks; i++) {
      if (!uploadedSet.has(i)) pendingChunks.push(i);
    }

    const inFlightBytes = new Map(); // chunkIndex → bytes sent so far
    let completedBytes = uploadedBytes;
    const reportProgress = () => {
      let inFlight = 0;
      for (const bytes of inFlightBytes.values()) inFlight += bytes;
      const total = Math.min(completedBytes + inFlight, fileToUpload.size);
      setProgress(Math.round((total / fileToUpload.size) * 100));
    };

    const uploadChunkAt = async (i) => {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, fileToUpload.size);
      const chunk = fileToUpload.slice(start, end);

      const chunkForm = new FormData();
      chunkForm.append('chunk', chunk, fileToUpload.name);
      chunkForm.append('fileId', fileId);
      chunkForm.append('chunkIndex', i);
      chunkForm.append('totalChunks', totalChunks);
      chunkForm.append('originalName', fileToUpload.name);
      chunkForm.append('mimeType', fileToUpload.type);
      // The server runs the minute gate on chunk 0 only; the hint lets it
      // size the whole upload from the first 10 MB.
      if (i === 0 && durationHintRef.current) chunkForm.append('durationSeconds', durationHintRef.current);

      await api.post('/api/upload/chunk', chunkForm, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: abortControllerRef.current?.signal,
        onUploadProgress: (progressEvent) => {
          inFlightBytes.set(i, Math.min(progressEvent.loaded, end - start));
          reportProgress();
        },
      });

      inFlightBytes.delete(i);
      completedBytes += (end - start);
      reportProgress();
    };

    // Chunk 0 goes alone first: the server runs the credit/limit check on it,
    // so a rejected upload fails after one chunk instead of a full batch.
    if (pendingChunks[0] === 0) {
      await uploadChunkAt(pendingChunks.shift());
    }

    let nextChunk = 0;
    await Promise.all(Array.from(
      { length: Math.min(UPLOAD_CONCURRENCY, pendingChunks.length) },
      async () => {
        while (nextChunk < pendingChunks.length) {
          if (abortControllerRef.current?.signal.aborted) return;
          await uploadChunkAt(pendingChunks[nextChunk++]);
        }
      }
    ));
    if (abortControllerRef.current?.signal.aborted) return null;

    // All chunks uploaded — assemble on the server and create the job
    return api.post('/api/upload/assemble', {
      fileId,
      ...(durationHintRef.current ? { durationSeconds: durationHintRef.current } : {}),
      maxWords: config.maxWords,
      subtitleLanguage: config.subtitleLanguage,
      audioCleanup: !!config.audioCleanup,
      styleOptions: JSON.stringify(styleOptions),
    }, {
      signal: abortControllerRef.current?.signal,
    });
  };

  const startUpload = async (fileToUpload, options = {}) => {
    resetJob();
    if (!(await checkCreditsBeforeUpload(options.driveUrl ? null : fileToUpload))) return;
    setStatus('uploading');
    setFileName(fileToUpload.name);
    abortControllerRef.current = new AbortController();

    const userMaxWords = options.maxWords || uploadConfig.maxWords || 3;
    const userSubtitleLanguage = options.subtitleLanguage || uploadConfig.subtitleLanguage || 'hinglish';
    const userAudioCleanup = options.audioCleanup ?? uploadConfig.audioCleanup ?? false;

    setStage(STAGE.upload);
    clarityTags({
      language: userSubtitleLanguage,
      fileType: fileToUpload.name.split('.').pop()?.toLowerCase(),
      fileSize: fileToUpload.size > 200e6 ? 'over200mb' : fileToUpload.size > 50e6 ? '50to200mb' : 'under50mb',
    });
    trackEvent('upload_started', { language: userSubtitleLanguage, maxWords: userMaxWords });

    // Default to the Ali Abdaal preset for new uploads. Pull its real style from TEMPLATES so the
    // full preset (box, colours, fill) seeds correctly — falling back to sensible values if missing.
    const defaultTemplate = TEMPLATES.find(t => t.id === DEFAULT_TEMPLATE_ID);
    const defaultStyle = defaultTemplate?.style || {};
    const config = {
        styleId: DEFAULT_TEMPLATE_ID,
        fontSize: defaultStyle.fontSize || 'M',
        color: defaultStyle.color || '#FFFFFF',
        maxWords: userMaxWords,
        karaoke: defaultStyle.karaokeMode ?? true,
        subtitleLanguage: userSubtitleLanguage,
        audioCleanup: !!userAudioCleanup,
    };

    const fontSizePx = typeof config.fontSize === 'string' ? (SIZE_MAP[config.fontSize] || 48) : config.fontSize;
    // Seed with the full default-template style (box, fill, colours, etc.) so the preset renders
    // completely even if the user exports without opening the editor. Falls back to the legacy
    // TEMPLATE_DEFAULTS map when the template isn't found.
    const templateStyle = defaultStyle && Object.keys(defaultStyle).length ? defaultStyle : (TEMPLATE_DEFAULTS[config.styleId] || {});

    const styleOptions = {
        ...templateStyle,
        id: config.styleId,
        fontSize: fontSizePx,
        color: config.color,
        karaokeMode: config.karaoke
    };

    if (fileToUpload.size === 0) {
      toast.error('Cannot upload an empty file.');
      resetJob();
      return;
    }

    // Capture a poster frame locally while the chunks upload, so the dashboard
    // card has a thumbnail the moment the job exists. Runs on the user's device
    // at zero server cost; the worker's ffmpeg poster replaces it on completion.
    const posterPromise = !options.driveUrl && fileToUpload.type.startsWith('video/')
      ? captureVideoPoster(fileToUpload)
      : Promise.resolve(null);

    try {
      // Fast path: presigned direct upload to S3 — the video bytes go straight to
      // the bucket near the user instead of crawling through the VPS. On any
      // infrastructure failure (S3 off, CORS, network to bucket) we fall back to
      // the legacy chunked upload below; business rejections surface immediately.
      let response = null;
      try {
        response = options.driveUrl
          ? await api.post('/api/upload/drive', { url: options.driveUrl, maxWords: config.maxWords, subtitleLanguage: config.subtitleLanguage, audioCleanup: !!config.audioCleanup, styleOptions: JSON.stringify(styleOptions) }, { signal: abortControllerRef.current?.signal, timeout: 660000 })
          : await directUploadToS3(fileToUpload, config, styleOptions);
      } catch (directErr) {
        if (directErr.code === 'ERR_CANCELED' || directErr.name === 'CanceledError') return;
        if (!directErr.directUploadFailed) throw directErr;
        console.warn('Direct S3 upload unavailable — falling back to chunked upload');
        trackEvent('upload_fallback_chunked');
        clarityTag('uploadRoute', 'vps');
        setProgress(0); // the transfer restarts from zero on the fallback route
      }

      if (!response) {
        response = await chunkedUploadToVPS(fileToUpload, config, styleOptions);
        if (!response) return; // aborted mid-upload
      }

      const { jobId: newJobId, mediaType: uploadMediaType } = response.data;

      if (response.data.styleWarning) {
        toast(response.data.styleWarning, { icon: '⚠️' });
      }

      setJobPreferences({ ...config, id: config.styleId, karaokeMode: config.karaoke, subtitleLanguage: config.subtitleLanguage });
      setFileName(fileToUpload.name);

      setJobId(newJobId);
      // Sync the ref eagerly — the useEffect that mirrors jobId into it only
      // runs after the next render, but the presigned poll below starts now.
      jobIdRef.current = newJobId;
      // The job id ties a recording to a row in the DB: with it, a support
      // message ("my export broke") becomes a replay plus its backend logs.
      clarityTag('jobId', newJobId);
      trackEvent('upload_succeeded', { reused: Boolean(response.data.reused) });
      setStatus('queued');
      setProgress(0); // Reset HTTP upload progress — SSE will now drive progress
      setMediaType(uploadMediaType === 'video' ? 'video' : 'audio');

      const serverSourceUrl = response.data.sourceUrl || `/api/download/source/${newJobId}`;
      const token = localStorage.getItem('auth_token') || '';
      // Keep URL relative so it routes through the Vite proxy (same-origin).
      // Do NOT prepend VITE_API_URL here — that creates a cross-origin URL which
      // Chrome's <video> element rejects even though opening it directly works.
      const authedSourceUrl = `${serverSourceUrl}${serverSourceUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
      const setPlayerUrl = uploadMediaType === 'video' ? setVideoUrl : setAudioUrl;
      setPlayerUrl(authedSourceUrl);

      // The editor opens right after upload, and the proxy URL above streams
      // S3 → VPS → browser over a congested consumer route — playback buffers
      // forever. Swap to a presigned S3 URL (bytes flow S3 → browser directly)
      // as soon as one is available. The job flips to storage='s3' only when
      // the background promote/copy lands, seconds AFTER the upload response,
      // so a single immediate fetch would usually miss — poll briefly instead.
      // Non-native containers (.mov/.mkv) stay on the proxy: it transcodes to
      // H.264, while a presigned URL would serve a raw file Chrome can't decode.
      const browserNative = /\.(mp4|webm|ogv|mp3|wav|m4a|aac|ogg)$/i.test(fileToUpload.name);
      if (browserNative) {
        (async () => {
          for (let attempt = 0; attempt < 10; attempt++) {
            if (jobIdRef.current !== newJobId) return; // user moved to another job
            try {
              const presignedRes = await api.get(`/api/download/presigned/${newJobId}`);
              // Prefer the low-res playback proxy when it already exists
              // (duplicate-upload reuse of a finished job) — exports still
              // burn from the original. Presigned URLs contain auth — no token.
              const d = presignedRes.data;
              const fastUrl = d?.playbackUrl || (d?.presigned ? d?.url : null);
              if (fastUrl) {
                if (jobIdRef.current === newJobId) setPlayerUrl(fastUrl);
                return;
              }
            } catch (_) { /* transient — keep polling */ }
            await new Promise((resolve) => setTimeout(resolve, 3000));
          }
        })();
      }

      // Fire-and-forget: send the locally-captured poster so the dashboard card
      // shows a thumbnail while the job processes. The server no-ops if the job
      // already has one (e.g. duplicate-upload reuse of a finished job).
      if (uploadMediaType === 'video') {
        posterPromise.then((p) => {
          if (!p) return;
          const form = new FormData();
          form.append('poster', p.blob, 'poster.jpg');
          form.append('width', String(p.width));
          form.append('height', String(p.height));
          return api.post(`/api/jobs/${newJobId}/poster`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }).catch(() => { /* poster is optional — never surface an error */ });
      }

      if (response.data.reused) {
        // Reused cached captions for an identical file. Flag it so the editor can ask
        // the user if there's a sync/accuracy issue and offer a fresh regenerate.
        setJustReusedJobId(newJobId);
        toast.success("Same file detected, so we reused your existing captions.");
      } else {
        toast.success("File uploaded! Processing in background…");
      }
      navigate(`/app/editor/${newJobId}`);
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;

      const data = err.response?.data;
      const msg = data?.error || "Upload failed. Please try again.";
      const code = data?.code;

      trackEvent('upload_failed', { code: code || err.response?.status || 'network' });
      clarityUpgrade('upload_failed');

      if (code === 'LIMIT_REACHED') {
        // Out of minutes — show the pack wall
        trackEvent('minutes_exhausted', { where: 'server' });
        setUpgradeWallReason(null);
        setUpgradeWallForced(true);
        setShowUpgradeModal(true);
        toast.error(msg, { duration: 5000 });
      } else if (code === 'UPLOAD_IN_PROGRESS') {
        // Another upload from this account is still being admitted. Nothing is
        // wrong with this file, so don't drop the user on the error screen.
        toast.error(msg, { duration: 5000 });
      } else if (code === 'VIDEO_TOO_LONG') {
        // The server's ffprobed verdict (the client hint may have been missing): same upsell
        // toast, with the cap and the Agency ceiling the 403 carries.
        const body = err.response?.data || {};
        showVideoTooLongToast({
          videoSeconds: durationHintRef.current || null,
          capMinutes: body.maxVideoMinutes || Math.round(MAX_DURATION_SECONDS / 60),
          ceilingMinutes: body.upgradeMaxVideoMinutes || Math.round(MAX_DURATION_SECONDS / 60),
          canUpgrade: !!body.upgradePack,
        });
      } else if (err.response?.status === 403 && (msg.includes('limit reached') || msg.includes('maximum length'))) {
        toast.error(msg, { duration: 5000 });
      } else {
        setError(msg);
        toast.error(msg);
      }
      resetJob();
    }
  };

  const handleFileSelect = (file, options = {}) => {
    if (status === 'uploading') return;
    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-m4a', 'audio/ogg', 'video/mpeg', 'video/x-mpeg', 'video/mp4', 'video/webm', 'video/quicktime', 'video/x-matroska'];
    const ext = file.name.split('.').pop().toLowerCase();
    const validExts = ['mpeg', 'mpg', 'mp3', 'wav', 'm4a', 'mp4', 'webm', 'mov', 'ogg', 'mkv'];

    if (!validTypes.includes(file.type) && !validExts.includes(ext)) {
      toast.error("Unsupported file type. Please upload MP4, MOV, M4A, MKV, MPEG, MPG, WebM, WAV, MP3, or OGG.");
      trackEvent('upload_file_rejected', { ext, mime: file.type || 'unknown' });
      return;
    }
    const normalizedFile = /\.(mpeg|mpg)$/i.test(file.name) && !file.type.startsWith('video/')
      ? new File([file], file.name, { type: 'video/mpeg', lastModified: file.lastModified }) : file;
    startUpload(normalizedFile, options);
  };

  return (
    <JobContext.Provider value={{
      jobId, setJobId,
      mediaDurationSeconds,
      status, setStatus,
      progress, setProgress,
      queuePosition, setQueuePosition,
      queueTotal, setQueueTotal,
      result, setResult,
      error, setError,
      audioUrl, setAudioUrl,
      audioProxyUrl, setAudioProxyUrl,
      mediaType, setMediaType,
      videoUrl, setVideoUrl,
      burning, setBurning,
      burnProgress, setBurnProgress,
      burnJobId, setBurnJobId,
      completedBurnUrl, setCompletedBurnUrl,
      contentSavedAt, setContentSavedAt,
      burnOutcome,
      burnOverlayVisible, setBurnOverlayVisible,
      burnFileName, setBurnFileName,
      requestRating, setRequestRating,
      startBurn,
      pauseBurnPolling,
      fileName, setFileName,
      jobPreferences, setJobPreferences,
      hasUnsavedChanges, setHasUnsavedChanges,
      refinedResult, setRefinedResult,
      justReusedJobId, setJustReusedJobId,
      showUpgradeModal, setShowUpgradeModal,
      upgradeWallForced, setUpgradeWallForced,
      upgradeWallReason, setUpgradeWallReason,
      uploadConfig, setUploadConfig,
      resetJob,
      handleDriveImport: (url, options = {}) => {
        if (status === 'uploading') return;
        return startUpload({ name: 'Importing Google Drive video', size: 1, type: 'video/mp4' }, { ...options, driveUrl: url });
      },
      handleFileSelect,
      startUpload
    }}>
      {children}
    </JobContext.Provider>
  );
};
